<template>
  <v-container fluid class="pa-0 bg-slate-50 min-h-screen" style="font-family: 'Inter', sans-serif;">
    <div class="p-4 md:p-8">
        <div class="max-w-4xl mx-auto">
        <!-- HEADER -->
        <div class="mb-8 flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-black text-slate-800 tracking-tight">{{ $route.name }}</h1>
            <p class="text-sm text-slate-500 font-medium">Preencha os dados da Nota Fiscal</p>
          </div>
          <v-btn icon to="/nfe" class="bg-white shadow-sm border border-slate-200">
            <v-icon color="slate-600">mdi-arrow-left</v-icon>
          </v-btn>
        </div>

        <!-- FORMULÁRIO -->
        <section class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-8 py-5 flex items-center gap-3" style="background-color: #333333;">
            <v-icon color="orange" small>mdi-file-document-edit-outline</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
              DADOS DO REGISTRO
            </h2>
          </div>
          
          <div class="p-10">
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-row>
                <v-col cols="12" sm="12" md="6">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Data e Hora</label>
                  <v-text-field
                    placeholder="dd/mm/YYYY HH:mm"
                    v-mask="'00/00/0000 00:00'"
                    v-model="data.datehour"
                    solo flat dense hide-details
                    class="custom-field-form"
                  />
                </v-col>
                <v-col cols="12" sm="12" md="6">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Município</label>
                  <v-autocomplete
                    v-model="data.prefecture_id"
                    placeholder="SELECIONE O MUNICÍPIO..."
                    :items="prefectures"
                    :rules="fieldRules"
                    item-text="prefecture_social_name"
                    item-value="prefecture_id"
                    solo flat dense hide-details
                    class="custom-field-form"
                    @change="
                      [
                        getItems(data.prefecture_id),
                        getVehicles(data.prefecture_id),
                        getProviders(data.prefecture_id),
                      ]
                    "
                  />
                </v-col>
                <v-col cols="12" sm="12" md="6">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Fornecedor</label>
                  <v-autocomplete
                    v-model="data.provider_id"
                    placeholder="SELECIONE O FORNECEDOR..."
                    :items="providers"
                    :rules="fieldRules"
                    item-text="provider_name"
                    item-value="provider_id"
                    solo flat dense hide-details
                    class="custom-field-form"
                  />
                </v-col>
                <v-col cols="12" sm="12" md="6">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Veículo</label>
                  <v-autocomplete
                    placeholder="SELECIONE O VEÍCULO..."
                    :rules="fieldRules"
                    :items="vehicles"
                    item-value="vehicle_id"
                    v-model="data.vehicle_id"
                    solo flat dense hide-details
                    class="custom-field-form"
                  >
                    <template slot="selection" slot-scope="{ item }"
                      >{{ item.brand_desc }} - {{ item.model_desc }} -
                      {{ item.vehicle_board }}</template
                    >
                    <template slot="item" slot-scope="{ item }"
                      >{{ item.brand_desc }} - {{ item.model_desc }} -
                      {{ item.vehicle_board }}</template
                    >
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" sm="12" md="12">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Item Licitado</label>
                  <v-autocomplete
                    placeholder="SELECIONE O ITEM..."
                    :rules="fieldRules"
                    :items="items"
                    item-value="item_id"
                    item-text="item_name"
                    v-model="data.item_id"
                    solo flat dense hide-details
                    class="custom-field-form"
                  >
                    <template slot="selection" slot-scope="{ item }"
                      >{{ item.item_description_desc }} — R$
                      {{ realMaskII(item.item_price.toFixed(3)) }}</template
                    >
                    <template slot="item" slot-scope="{ item }"
                      >{{ item.item_description_desc }} — R$
                      {{ realMaskII(item.item_price.toFixed(3)) }}</template
                    >
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" sm="12" md="3">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Preço Unit.</label>
                  <v-text-field
                    placeholder="0,00"
                    v-model="data.supply_bidding_price"
                    v-mask-decimal.br="2"
                    solo flat dense hide-details
                    class="custom-field-form"
                    @blur="calculateTotal"
                  />
                </v-col>
                <v-col cols="12" sm="12" md="3">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Litros</label>
                  <v-text-field
                    placeholder="0,00"
                    v-model="data.supply_liters_stocked"
                    v-mask-decimal.br="2"
                    solo flat dense hide-details
                    class="custom-field-form"
                    @input="calculateTotal"
                  />
                </v-col>
                <v-col cols="12" sm="12" md="3">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Valor Total</label>
                  <v-text-field
                    placeholder="0,00"
                    v-model="data.supply_bidding_value"
                    v-mask-decimal.br="2"
                    solo flat dense hide-details
                    class="custom-field-form"
                    @blur="calculatePrice"
                  />
                </v-col>
                <v-col cols="12" sm="12" md="3">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Nº Nota Fiscal</label>
                  <v-text-field
                    placeholder="000000"
                    v-model="data.supply_coupon"
                    v-mask-number
                    solo flat dense hide-details
                    class="custom-field-form"
                  />
                </v-col>
              </v-row>
            </v-form>
          </div>

          <div class="px-8 py-6 bg-slate-50 flex justify-end gap-3 border-t border-slate-100">
            <button @click="$router.push('/nfe')" class="px-6 py-2 text-[10px] font-black text-slate-400 hover:text-slate-600 transition-colors uppercase tracking-[0.2em]">Cancelar</button>
            <button 
              @click="validate" 
              :disabled="!valid && loader" 
              class="bg-slate-800 hover:bg-slate-950 text-white font-black text-[10px] uppercase tracking-[0.2em] px-10 py-3.5 rounded-xl shadow-lg flex items-center gap-2 group transition-all"
            >
              <v-icon v-if="loader" small class="animate-spin text-white">mdi-loading</v-icon>
              <span>{{ id ? 'ATUALIZAR' : 'CADASTRAR' }} NOTA FISCAL</span>
            </button>
          </div>
        </section>
      </div>
    </div>
    <v-snackbar
      multi-line
      :color="snack.color"
      v-model="snack.show"
      transition="fade-transition"
    >
      {{ snack.message }}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
export default {
  props: ["id"],
  mixins: [Helpers],
  data: () => ({
    showPassword: false,
    password: "",
    copiado: false,
    valid: false,
    loader: false,
    showForm: false,
    providers: [],
    drivers: [],
    vehicles: [],
    prefectures: [],
    items: [],
    driver_name: "",
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      supply_id: null,
      datehour: null,
      supply_bidding_price: 0,
      supply_liters_stocked: 0,
      supply_bidding_value: 0,
      supply_coupon: 0,
      item_id: null,
      prefecture_id: null,
      vehicle_id: null,
      provider_id: null,
    },
  }),
  created() {
    this.Helpers;
    this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
    this.loader = false;
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`/billings/nfes/${this.id}`).then((r) => {
        this.data = r.data[0];
        this.data.datehour = this.convertDateTime(r.data[0].datehour);
        this.data.supply_liters_stocked = this.data.supply_liters_stocked.toFixed(
          2
        );
        this.data.supply_bidding_value = this.data.supply_bidding_value.toFixed(
          2
        );
        this.data.supply_bidding_price = this.data.supply_bidding_price.toFixed(
          2
        );
        this.getVehicles(r.data[0].prefecture_id);
        this.getItems(r.data[0].prefecture_id);
        this.getProviders(r.data[0].prefecture_id);
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  methods: {
    calculateTotal() {
      if (
        this.data.supply_liters_stocked &&
        this.data.supply_bidding_price &&
        parseFloat(this.data.supply_liters_stocked) > 0 &&
        parseFloat(this.data.supply_bidding_price) > 0
      ) {
        let liters = this.convertDecimalNumberIII(
          this.data.supply_liters_stocked
        );
        let item_price = this.convertDecimalNumberIII(
          this.data.supply_bidding_price
        );
        this.data.supply_bidding_value = (
          parseFloat(liters) * parseFloat(item_price)
        ).toFixed(2);
      }
    },
    calculatePrice() {
      if (
        this.data.supply_bidding_value &&
        this.data.supply_liters_stocked &&
        parseFloat(this.data.supply_bidding_value) > 0 &&
        parseFloat(this.data.supply_liters_stocked) > 0
      ) {
        let liters = this.convertDecimalNumberIII(
          this.data.supply_liters_stocked
        );
        let value = this.convertDecimalNumberIII(
          this.data.supply_bidding_value
        );
        this.data.supply_bidding_price = (
          parseFloat(value) / parseFloat(liters)
        ).toFixed(2);
      }
    },
    getProviders() {
      this.providers = [];
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(`/readjust/providers/${this.data.prefecture_id}`)
        .then((r) => {
          this.providers = r.data;
          this.snack.color = "success";
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    getVehicles() {
      this.vehicles = [];
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(`/distribution/vehicles/prefecture/${this.data.prefecture_id}`)
        .then((r) => {
          this.vehicles = r.data;
          this.snack.color = "success";
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    getItems() {
      this.items = [];
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(`/billings/nfes/items/${this.data.prefecture_id}`)
        .then((r) => {
          this.items = r.data;
          this.snack.color = "success";
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    loadItems(provider_id) {
      this.$http
        .get(`/supplies/providers/${provider_id}`)
        .then((r) => (this.items = r.data));
    },
    validate() {
      this.snack.show = true;
      this.loader = true;
      if (this.$refs.form.validate()) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message =
          "Atencão um ou mais campos precisam ser preenchidos!";
        this.loader = false;
      }
    },
    createOrUpdate() {
      this.loader = true;
      if (this.id) {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .put(`/billings/nfes/${this.id}`, this.data)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.loader = true;
            setTimeout(() => {
              this.$router.push("/nfe");
            }, 1500);
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      } else {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .post("/billings/nfes", this.data)
          .then((r) => {
            setTimeout(() => {
              this.$router.push("/nfe");
            }, 1500);
            this.showPassword = true;
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = false;
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style scoped>
.custom-field-form >>> .v-input__slot {
  background-color: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  padding: 0 16px !important;
  height: 48px !important;
}

.custom-field-form >>> .v-text-field__slot input {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #1e293b !important;
}

.custom-field-form >>> .v-select__selection {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #1e293b !important;
}
</style>
